import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Observer, Subject } from 'rxjs';
import { B2BFingerSize } from 'src/app/interfaces/b2b-finger-size';
import { B2BInput } from 'src/app/interfaces/b2b-input';
import { B2BOutput } from 'src/app/interfaces/b2b-output';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { Size } from 'src/app/interfaces/size';
import { ItemDescriptionService } from 'src/app/services/item-description/item-description.service';
import { TimeProviderService } from 'src/app/providers/time/time.provider.service';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { CustomerService } from 'src/app/services/customer/customer.service';
import { IframeService } from 'src/app/services/iframe/iframe.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { SizesService } from 'src/app/services/sizes/sizes.service';
import { SkuMapService } from 'src/app/services/sku-map/sku-map.service';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { assert, assertTrue } from 'src/app/utils/assert.util';
import { formatFingerSizeForB2BSKU } from 'src/app/utils/configured-item.util';

@Injectable({
  providedIn: 'root'
})
export class CheckoutService {
  private iframeService = inject(IframeService);
  private rulesService = inject(RulesService);
  private stoneArrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  private stoneTypesWithShapesService = inject(StoneTypesWithShapesService);
  private itemDescriptionService = inject(ItemDescriptionService);
  private sizesService = inject(SizesService);
  private timeProvider = inject(TimeProviderService);
  private skuMapService = inject(SkuMapService);
  private configuredItemService = inject(ConfiguredItemService);
  private customerService = inject(CustomerService);
  private _b2bDataSubject = new Subject<B2BInput>();
  private _b2bData: B2BInput | null = null;
  get b2bData() {
    return Object.assign({}, this._b2bData);
  }
  price: number | null = null;
  retailerIframeID: string | null = null;
  ringBuilderID: string | null = null;

  get doesHaloItemNumberHaveFingerSize() {
    return !!this._b2bData?.styleNumber?.match(/\S*[0-9]+\.?[0-9]*$/);
  }

  originalSKU: string | null = null;
  retailerName = signal<string | null>(null);
  shouldShowConsumerSiteShare = signal(false);
  shareMessageOverride = signal<string | null>(null);
  baseShareURLOverride = signal<string | null>(null);
  isRetailerCartEnabled = signal(false);
  isRetailerOnly = signal(false);
  shouldEnableAnalytics = signal(false);
  analyticsAllowed = computed(() => !this.isRetailerOnly() || this.shouldEnableAnalytics() === true);

  constructor() {
    (window as any).checkoutService = this;

    effect(() => {
      const latestMessage = this.iframeService.latestMessage();

      if (latestMessage === 'Benchmark::RingLab::enablecart') {
        this.isRetailerCartEnabled.set(true);
      } else if (latestMessage) {
        try {
          const messageObject = JSON.parse(latestMessage);
          if (messageObject.type === 'Benchmark::RingLab::sharetext') {
            this.shareMessageOverride.set(messageObject.value);
          } else if (messageObject.type === 'Benchmark::RingLab::shareurl') {
            this.baseShareURLOverride.set(messageObject.value);
          } else if (messageObject.type === 'Benchmark::RingLab::shouldshowconsumersiteshare') {
            this.shouldShowConsumerSiteShare.set(messageObject);
          } else if (messageObject.type === 'Benchmark::RingLab::retailername') {
            this.retailerName.set(messageObject.value);
          }
        } catch { }
      }
    }, { allowSignalWrites: true });
  }

  async setB2BInfoFromURL(url: URL) {
    const data = url.searchParams.get('l');
    assert(data, 'No b2b data');

    const decodedData = atob(data);
    this._b2bData = JSON.parse(decodedData);
    assert(this._b2bData, 'Unexpected B2B data format');

    this.retailerIframeID = this._b2bData.retailerIframeID ?? null;
    this.ringBuilderID = this._b2bData.customerToken ?? null;
    this.isRetailerOnly.set(!this.ringBuilderID);
    this.shouldEnableAnalytics.set(this._b2bData.shouldEnableAnalytics === true);

    // Start resolving the retailer iframe key now rather than at add-to-cart. In a B2B session it
    // is not in the payload at all and has to be fetched with the customerToken, so kicking it off
    // here means the value is normally already in hand by the time checkout needs it.
    // Deliberately not awaited — nothing on this path needs it yet, and the resolution is memoised.
    this.customerService.resolveIframeID(this._b2bData);
  }

  updateB2BData(mutator: (b2bData: B2BInput) => void) {
    assert(this._b2bData, 'No b2b data');
    mutator(this._b2bData);
    if (this._b2bData.eng) {
      this._b2bData.eng = encodeURIComponent(decodeURIComponent(this._b2bData.eng));
    }
    const b2bDataString = JSON.stringify(this.b2bData);
    const encodedPayload = btoa(b2bDataString);
    const url = new URL(window.location.href);
    url.searchParams.set('l', encodedPayload);
    window.history.replaceState(null, '', url);
    this._b2bDataSubject.next(this._b2bData);
  }

  /**
   * The item number sent to B2B. Shared by getOutputData and prefetchDescription so the prefetch
   * cannot warm the cache under a different key than the one checkout later looks up.
   */
  getItemNumber(selectedItem: ConfiguredItem, isQuarterSize: boolean) {
    assert(selectedItem.size, 'No finger size selected');
    const b2bFingerSize = this.configuredItemService.getB2BFingerSizeObject(selectedItem.size, isQuarterSize);
    const styleNumber = this.configuredItemService.getNewStyleFromSelectedAttributes(selectedItem);
    return this.getHaloSKU(selectedItem.size, isQuarterSize) ?? `${styleNumber}-${formatFingerSizeForB2BSKU(b2bFingerSize)}`;
  }

  /**
   * Warm the description cache when the review step opens, so Add to Cart awaits a settled promise
   * instead of issuing a request at the click. Add to Cart is only reachable after review, so this
   * covers the whole path. Fire and forget: failures are handled where the value is consumed.
   */
  async prefetchDescription(selectedItem: ConfiguredItem, isQuarterSize: boolean) {
    try {
      const retailerIframeID = await this.customerService.resolveIframeID(this._b2bData);
      this.itemDescriptionService.prefetch(this.getItemNumber(selectedItem, isQuarterSize), retailerIframeID);
    } catch (e) {
      console.error('Could not prefetch the item description', e);
    }
  }

  async getOutputData(selectedItem: ConfiguredItem, po: string | null, isQuarterSize: boolean) {
    assert(this._b2bData, 'No b2b data');

    assert(selectedItem.size, 'No finger size selected');
    const b2bFingerSize = this.configuredItemService.getB2BFingerSizeObject(selectedItem.size, isQuarterSize);

    assert(this.price, 'No price calculated');

    const itemNumber = this.getItemNumber(selectedItem, isQuarterSize);

    // The API owns this format (ADO #8976). It used to be assembled here from the width, design,
    // finger size and stone details, which meant the same format lived in two languages and could
    // drift silently.
    //
    // The key must come from CustomerService, not from the payload: a B2B session carries a
    // customerToken and NO retailerIframeID, so reading the payload field directly yields undefined
    // and Angular's HttpHeaders throws on it, taking add-to-cart down with it. Resolution starts in
    // setB2BInfoFromURL and is memoised, so this normally awaits an already-settled promise.
    //
    // Normally already cached by prefetchDescription() when the review step opened. Never throws —
    // a failed lookup yields FALLBACK_ITEM_DESCRIPTION rather than blocking checkout.
    const retailerIframeID = await this.customerService.resolveIframeID(this._b2bData);
    const description = await this.itemDescriptionService.get(itemNumber, retailerIframeID);

    const serverDateTime = await this.timeProvider.get();

    const outputData: B2BOutput = {
      lineId: this._b2bData?.lineId ?? null,
      eng: selectedItem.engravingText && selectedItem.engravingType ? encodeURIComponent(selectedItem.engravingText) : null,
      engFont: selectedItem.engravingText && selectedItem.engravingType ? selectedItem.engravingType.displayName.toUpperCase() : null,
      po: !!po ? encodeURIComponent(po) : null,
      qty: 1,
      item: itemNumber,
      // The endpoint returns it untruncated; B2B's 50-character field limit applies here.
      description: description.substring(0, 50),
      unitPrice: Math.round((this.price + Number.EPSILON) * 100) / 100,
      fingerSize: b2bFingerSize.size,
      qtrSize: b2bFingerSize.isQuarterSize,
      requestDate: serverDateTime,
      originalItem: this._b2bData.styleNumber,
      customerToken: this._b2bData.customerToken,
      customer: this._b2bData.customer,
      img: undefined
    };

    return outputData;
  }

  getHaloSKU(size: Size | number, roundToHalf?: boolean) {
    const haloStyleNumber = this._b2bData?.haloStyle;
    if (haloStyleNumber) {
      return this.configuredItemService.buildHaloSKU(haloStyleNumber, size, roundToHalf);
    }
    return undefined;
  }

  skuCompare(sku: string, sku2: string) {
    const skuSplit = sku.split('')
    const sku2Split = sku2.split('')

    let count = 0

    for (let i = 0; i < skuSplit.length; i++) {
      if (skuSplit[i] !== sku2Split[i] || i >= sku2Split.length) {
        return count;
      }
      count++
    }
    return count;
  }

  subscribeToB2BDataChanges(observerOrNext?: Partial<Observer<B2BInput>> | ((value: B2BInput) => void)) {
    return this._b2bDataSubject.subscribe(observerOrNext);
  }

  async getSimilarHaloSKUToSelected(styleNumber: string, selectedHaloStyleNumberOverride?: string): Promise<string>;
  async getSimilarHaloSKUToSelected(configuredItem: ConfiguredItem, selectedHaloStyleNumberOverride?: string): Promise<string>
  async getSimilarHaloSKUToSelected(configuredItemOrStyleNumber: ConfiguredItem | string, selectedHaloStyleNumberOverride?: string): Promise<string | undefined> {
    const b2bData = this._b2bData;
    assert(b2bData, 'No b2b data');

    if (this.sizesService.values().length === 0) {
      await this.sizesService.cacheData(this.retailerIframeID);
    }
    const customizerStyle = typeof configuredItemOrStyleNumber === 'object' ? this.configuredItemService.getNewStyleFromSelectedAttributes(configuredItemOrStyleNumber) : configuredItemOrStyleNumber;

    const size = ((typeof configuredItemOrStyleNumber === 'object') ? configuredItemOrStyleNumber.size : this.sizesService.values().find(s => s.value === b2bData.fingerSize)) ?? (await this.getBestAvailableFingerSize(typeof configuredItemOrStyleNumber === 'object' ? configuredItemOrStyleNumber : this.configuredItemService.getAttributesFromNewStyleNumber(configuredItemOrStyleNumber)));
    assert(size, 'No size for configured item or style');

    const skuMapEntries = await this.skuMapService.getReverse(customizerStyle, size?.value);
    const selectedHaloStyleNumber = selectedHaloStyleNumberOverride ?? b2bData.selectedHaloStyleNumber;
    if (selectedHaloStyleNumber) {
      const haloStyle = skuMapEntries.sort((a,b) => this.skuCompare(selectedHaloStyleNumber, b.halosku) - this.skuCompare(selectedHaloStyleNumber, a.halosku))[0]?.halosku
      return haloStyle;
    }
    return skuMapEntries[0]?.halosku;
  }

  async getBestAvailableFingerSize(attributes: ConfiguredItem) {
    const availableSizes = await this.rulesService.getSizes(attributes, this.retailerIframeID);
    const mappedAvailableSizes = availableSizes.map(availableSize => this.sizesService.values().find(size => size.id === availableSize));
    mappedAvailableSizes.sort((a, b) => {
      const aValue = a?.value ?? 0;
      const bValue = b?.value ?? 0;
      const diff = Math.abs(8 - (aValue)) - Math.abs(8 - (bValue));
      if (diff === 0) {
        return bValue - aValue;
      }
      return diff;
    });
    const firstAvailableSize = mappedAvailableSizes[0];
    assert(firstAvailableSize, 'No matching size found');
    return firstAvailableSize;
  }
}
